/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Pause, Trash2, Volume2 } from 'lucide-react';

interface AudioRecorderProps {
  initialAudioUrl?: string;
  initialDuration?: number;
  onSave: (audioUrl: string, duration: number) => void;
  onDelete?: () => void;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
  initialAudioUrl,
  initialDuration = 0,
  onSave,
  onDelete,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | undefined>(initialAudioUrl);
  const [duration, setDuration] = useState<number>(initialDuration);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [recordSeconds, setRecordSeconds] = useState(0);

  // Web Audio Context & Recorder refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Graphical Frequency Analyzer for recording
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  useEffect(() => {
    // If we have a stored audio URL, initialize total duration when available
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.addEventListener('loadedmetadata', () => {
        setDuration(audio.duration || 0);
      });
    }

    return () => {
      stopAllStreamsAndTimers();
    };
  }, [audioUrl]);

  const stopAllStreamsAndTimers = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
  };

  // Start microphone capture
  const startRecording = async () => {
    audioChunksRef.current = [];
    setRecordSeconds(0);
    setCurrentTime(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create Web Audio Analyser for dynamic sound wave graph
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioCtx();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const sourceNode = audioContext.createMediaStreamSource(stream);
      sourceNode.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
      sourceNodeRef.current = sourceNode;

      // Start recording
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const newUrl = URL.createObjectURL(audioBlob);
        setAudioUrl(newUrl);

        // Compute exact audio duration
        const tempAudio = new Audio(newUrl);
        tempAudio.addEventListener('loadedmetadata', () => {
          const finalDuration = tempAudio.duration || recordSeconds;
          setDuration(finalDuration);
          onSave(newUrl, finalDuration);
        });

        // Close stream tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start clock trigger
      timerIntervalRef.current = setInterval(() => {
        setRecordSeconds((prev) => prev + 1);
      }, 1000);

      // Run canvas visually
      drawWaveform();

    } catch (err) {
      console.error('Microphone access denied or audio not supported:', err);
      mockRecordingAnimation();
    }
  };

  const mockRecordingAnimation = () => {
    setIsRecording(true);
    setRecordSeconds(0);
    timerIntervalRef.current = setInterval(() => {
      setRecordSeconds((prev) => prev + 1);
    }, 1000);
    drawMockWaves();
  };

  const drawMockWaves = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let step = 0;
    const render = () => {
      if (!canvasRef.current) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#6366f1';

      step += 0.1;
      for (let i = 0; i < 40; i++) {
        const height = 15 + Math.sin(step + i * 0.4) * 12 + Math.cos(step * 0.5 + i * 0.8) * 6;
        const x = (canvas.width / 40) * i;
        ctx.fillRect(x, canvas.height / 2 - height / 2, 4, height);
      }
      animationFrameRef.current = requestAnimationFrame(render);
    };
    render();
  };

  const drawWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;
    if (!ctx || !analyser || !dataArray) return;

    const render = () => {
      if (!canvasRef.current) return;
      analyser.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / dataArray.length) * 1.5;
      let x = 0;

      for (let i = 0; i < dataArray.length; i++) {
        const value = dataArray[i];
        const percent = value / 256;
        const height = Math.max(canvas.height * percent, 6);

        // Orange-Purple brand theme gradient indicators
        ctx.fillStyle = `rgb(${155 - i * 0.5}, ${115 + i}, 241)`;
        ctx.fillRect(x, canvas.height / 2 - height / 2, barWidth - 1, height);

        x += barWidth;
      }

      animationFrameRef.current = requestAnimationFrame(render);
    };

    render();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    stopAllStreamsAndTimers();
    setIsRecording(false);
  };

  // Playback Operations
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    setCurrentTime(value);
    if (audioRef.current) {
      audioRef.current.currentTime = value;
    }
  };

  const deleteRecording = () => {
    stopAllStreamsAndTimers();
    setIsPlaying(false);
    setAudioUrl(undefined);
    setDuration(0);
    setCurrentTime(0);
    if (onDelete) onDelete();
  };

  // Helper formatting mm:ss
  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-800 flex flex-col justify-center items-center" id="audio-recorder-module">
      {/* Waveform Visualization Box */}
      {isRecording && (
        <div className="w-full flex flex-col items-center gap-2 mb-4">
          <canvas
            ref={canvasRef}
            width={320}
            height={70}
            className="w-full bg-slate-100 dark:bg-slate-950/20 rounded-lg max-h-[70px]"
          />
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
            <span className="font-mono text-sm text-slate-600 dark:text-slate-400 font-bold">
              Recording - {formatTime(recordSeconds)}
            </span>
          </div>
        </div>
      )}

      {/* Main controllers */}
      {!audioUrl && !isRecording && (
        <div className="flex flex-col items-center py-6 text-center">
          <button
            type="button"
            onClick={startRecording}
            className="w-16 h-16 rounded-full bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 shadow-md hover:shadow-lg transition-transform hover:scale-105"
            id="start-recording-btn"
          >
            <Mic className="w-7 h-7" />
          </button>
          <span className="text-xs text-slate-500 mt-3 font-medium">
            Allow mic and click to record voice card
          </span>
        </div>
      )}

      {isRecording && (
        <div className="py-2">
          <button
            type="button"
            onClick={stopRecording}
            className="w-14 h-14 rounded-full bg-rose-600 text-white flex items-center justify-center hover:bg-rose-700 shadow-md transition-all animate-pulse"
            id="stop-recording-btn"
          >
            <Square className="w-5 h-5 fill-white" />
          </button>
        </div>
      )}

      {audioUrl && !isRecording && (
        <div className="w-full flex flex-col gap-3 py-1 bg-white dark:bg-slate-950/20 p-3.5 rounded-lg border border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              className="w-10 h-10 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white flex items-center justify-center"
              id="playback-toggle-btn"
            >
              {isPlaying ? <Pause className="w-4 h-4 fill-white" /> : <Play className="w-4 h-4 fill-white ml-0.5" />}
            </button>

            <div className="flex-1 flex flex-col">
              <div className="flex justify-between text-[11px] text-slate-500 font-mono">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
              <input
                type="range"
                min={0}
                max={duration || 100}
                step={0.1}
                value={currentTime}
                onChange={handleSliderChange}
                className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
              />
            </div>

            <button
              type="button"
              onClick={deleteRecording}
              className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-955/20 rounded-lg transition-colors"
              title="Delete audio card"
              id="playback-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 justify-center text-xs text-slate-400 font-medium">
            <Volume2 className="w-3.5 h-3.5" />
            <span>Voice recorded successfully. Click play or record new text.</span>
          </div>

          {/* Hidden audio element */}
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleAudioEnded}
          />
        </div>
      )}
    </div>
  );
};
